/**
 * VisualWorkspaceDesigner — Main shell for the drag-and-drop workspace designer.
 *
 * Three-panel layout:
 *   Left:   ComponentPalette (draggable items)
 *   Center: DesignerCanvas (drop zones, visual representation)
 *   Right:  PropertiesPanel (context-sensitive configuration)
 *
 * Wraps everything in DndContext and DesignerStateProvider.
 */
import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { Box, Snackbar, Alert, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';
import Editor from '@monaco-editor/react';
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';

import { DesignerStateProvider, useDesignerDispatch, useDesignerState, serializeToJson } from './DesignerStateContext';
import DesignerToolbar from './DesignerToolbar';
import ComponentPalette from './ComponentPalette';
import DesignerCanvas from './DesignerCanvas';
import PropertiesPanel from './PropertiesPanel';

// ---------------------------------------------------------------------------
// DnD drop handler
// ---------------------------------------------------------------------------
function useDndDropHandler() {
  const dispatch = useDesignerDispatch();
  const designer = useDesignerState();

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over || !active?.data?.current) return;

      const dragData = active.data.current;
      const dropId = over.id;

      // Parse drop zone: "drop-{zone}-{viewId}"
      const dropMatch = String(dropId).match(/^drop-(\w+)-(.+)$/);
      if (!dropMatch) return;

      const [, zone, viewId] = dropMatch;

      // Panel drop
      if (zone === 'panel' && dragData.type === 'panel') {
        dispatch({
          type: 'ADD_PANEL',
          viewId,
          panel: {
            type: dragData.panelType,
            role: dragData.role || null,
            entity: '',
            function: 'List',
            primaryKey: 'id',
            displayField: '',
            columns: [],
            ...(dragData.panelType === 'detail-grid' && {
              label: 'Details',
              parentKey: '',
              foreignKey: '',
              titleTemplate: '',
            }),
            ...(dragData.panelType === 'form' && {
              submitScript: '',
            }),
            ...(dragData.panelType === 'kpi' && {
              metricLabel: 'Metric',
            }),
            ...(dragData.panelType === 'chart' && {
              chartType: 'bar',
              chartTitle: 'Chart',
            }),
          },
        });
      }

      // Field-type drop onto panel zone → add column placeholder with fieldType, no fieldName yet
      if (zone === 'panel' && dragData.type === 'field-type') {
        const view = designer.views.find((v) => v.viewId === viewId);
        if (!view) return;
        const targetPanel =
          view.panels.find((p) => p.panelId === designer.selection.panelId && (p.type === 'grid' || p.type === 'detail-grid')) ||
          view.panels.find((p) => p.type === 'grid' || p.type === 'detail-grid');
        if (targetPanel) {
          dispatch({
            type: 'ADD_COLUMN',
            panelId: targetPanel.panelId,
            column: {
              fieldName: '',
              fieldType: dragData.fieldType,
              width: '',
              sortable: true,
            },
          });
        }
      }

      // Component-ref drop onto panel zone → add as a ref view
      if (zone === 'panel' && dragData.type === 'component-ref') {
        // If the component type is VIEW, add as a new view tab (ref)
        if (dragData.componentType === 'VIEW') {
          dispatch({
            type: 'ADD_REF_VIEW',
            componentCode: dragData.componentCode,
            componentType: dragData.componentType,
            displayName: dragData.displayName,
          });
        }
        // PICKER and PANEL components could also be handled here for inline embedding
        // For now, only VIEW components are added as ref views
      }

      // Field-type drop onto filter zone → add placeholder filter chip
      if (zone === 'filter' && dragData.type === 'field-type') {
        dispatch({
          type: 'ADD_FILTER_CHIP',
          viewId,
          chip: {
            label: dragData.displayLabel || dragData.fieldType,
            field: '',
            fieldType: dragData.fieldType,
            value: '',
            color: 'default',
            default: false,
          },
        });
      }

      // Field-type drop onto statusbar zone → add placeholder counter
      if (zone === 'statusbar' && dragData.type === 'field-type') {
        dispatch({
          type: 'ADD_COUNTER',
          viewId,
          counter: {
            label: dragData.displayLabel || dragData.fieldType,
            type: 'filtered',
            field: '',
            fieldType: dragData.fieldType,
            value: '',
            color: 'default',
          },
        });
      }
    },
    [dispatch, designer]
  );

  return handleDragEnd;
}

// ---------------------------------------------------------------------------
// Inner component (needs context)
// ---------------------------------------------------------------------------
function DesignerInner({ workspace, onSave, onClose, colors }) {
  const dispatch = useDesignerDispatch();
  const designer = useDesignerState();
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const handleDragEnd = useDndDropHandler();
  const [activeDrag, setActiveDrag] = useState(null);

  // JSON editor dialog
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
  const [jsonValue, setJsonValue] = useState('');
  const [jsonError, setJsonError] = useState(null);

  const handleOpenJson = useCallback(() => {
    const json = serializeToJson(designer);
    setJsonValue(JSON.stringify(json, null, 2));
    setJsonError(null);
    setJsonDialogOpen(true);
  }, [designer]);

  const handleApplyJson = useCallback(() => {
    try {
      const parsed = JSON.parse(jsonValue);
      // Re-load the designer state from the edited JSON
      dispatch({
        type: 'LOAD_FROM_JSON',
        workspace: { ...workspace, definition: parsed },
        definition: parsed,
      });
      setJsonDialogOpen(false);
      setMessage({ type: 'success', text: 'JSON applied to designer' });
    } catch (e) {
      setJsonError('Invalid JSON: ' + e.message);
    }
  }, [jsonValue, dispatch, workspace]);

  const handleFormatJson = useCallback(() => {
    try {
      const parsed = JSON.parse(jsonValue);
      setJsonValue(JSON.stringify(parsed, null, 2));
      setJsonError(null);
    } catch (e) {
      setJsonError('Invalid JSON: ' + e.message);
    }
  }, [jsonValue]);

  // Load workspace definition into state
  useEffect(() => {
    dispatch({
      type: 'LOAD_FROM_JSON',
      workspace,
      definition: workspace?.definition,
    });
  }, [workspace, dispatch]);

  // Track workspace id (may start null for new workspaces, set after first create)
  const [workspaceId, setWorkspaceId] = useState(workspace?.id || null);

  // Save handler — POST for new workspaces, PUT for existing
  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const json = serializeToJson(designer);
      const defJson = JSON.stringify(json, null, 2);
      const token = localStorage.getItem('waide_access_token');
      const isNew = !workspaceId;

      let url, method, body;

      if (isNew) {
        // Create new workspace — POST with full metadata
        url = '/api/v1/workspaces/manage';
        method = 'POST';
        const code = designer.workspace.code || designer.workspace.title
          .toUpperCase().replace(/[^A-Z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
        body = JSON.stringify({
          workspaceCode: code,
          title: designer.workspace.title || 'New Workspace',
          icon: designer.workspace.icon || 'ViewModule',
          domain: designer.workspace.domain || 'WMS',
          requiredAccessLevel: designer.workspace.accessLevel || 'READ_ONLY',
          definition: defJson,
          isActive: true,
        });
      } else {
        // Update existing workspace — PUT with definition
        url = `/api/v1/workspaces/manage/${workspaceId}`;
        method = 'PUT';
        body = JSON.stringify({ definition: defJson });
      }

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body,
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${response.status}`);
      }

      // For new workspaces, capture the returned id so subsequent saves use PUT
      if (isNew) {
        const result = await response.json();
        if (result.id) {
          setWorkspaceId(result.id);
        }
      }

      dispatch({ type: 'MARK_SAVED' });
      setMessage({ type: 'success', text: isNew ? 'Workspace created' : 'Workspace saved' });
      if (onSave) onSave();
    } catch (err) {
      setMessage({ type: 'error', text: `Save failed: ${err.message}` });
    } finally {
      setSaving(false);
    }
  }, [designer, workspaceId, onSave, dispatch]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        dispatch({ type: 'UNDO' });
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        dispatch({ type: 'REDO' });
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [dispatch, handleSave]);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={(event) => setActiveDrag(event.active?.data?.current || null)}
      onDragEnd={(event) => { handleDragEnd(event); setActiveDrag(null); }}
      onDragCancel={() => setActiveDrag(null)}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        <DesignerToolbar
          onClose={onClose}
          onSave={handleSave}
          onPreview={() => setMessage({ type: 'info', text: 'Preview coming in Phase 6' })}
          onViewJson={handleOpenJson}
          saving={saving}
          colors={colors}
        />

        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          <ComponentPalette colors={colors} />
          <DesignerCanvas colors={colors} />
          <PropertiesPanel colors={colors} />
        </Box>
      </Box>

      {/* Notifications */}
      <Snackbar
        open={!!message}
        autoHideDuration={3000}
        onClose={() => setMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {message && (
          <Alert severity={message.type} onClose={() => setMessage(null)} variant="filled" sx={{ minWidth: 300 }}>
            {message.text}
          </Alert>
        )}
      </Snackbar>

      {/* Drag overlay — follows cursor during drag */}
      <DragOverlay dropAnimation={null}>
        {activeDrag && (
          <Chip
            label={
              activeDrag.type === 'component-ref'
                ? activeDrag.displayName || activeDrag.componentCode
                : activeDrag.type === 'panel'
                ? activeDrag.panelType
                : activeDrag.type === 'field-type'
                ? activeDrag.displayLabel || activeDrag.fieldType
                : activeDrag.type === 'action'
                ? activeDrag.label || activeDrag.actionType
                : activeDrag.layoutType || 'item'
            }
            size="small"
            sx={{
              bgcolor: activeDrag.type === 'component-ref' ? '#60a5fa' : '#22c55e',
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.75rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            }}
          />
        )}
      </DragOverlay>

      {/* JSON Editor Dialog */}
      <Dialog
        open={jsonDialogOpen}
        onClose={() => setJsonDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: colors?.surface || '#1a1a2e',
            borderRadius: 1,
            border: `1px solid ${colors?.border || '#333'}`,
            height: '85vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            color: colors?.text || '#e0e0e0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1,
          }}
        >
          <span>Workspace JSON</span>
          <Typography variant="caption" sx={{ color: colors?.textSecondary || '#888' }}>
            Edit JSON directly — changes apply when you click Apply
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', p: 0, overflow: 'hidden' }}>
          {jsonError && <Alert severity="error" sx={{ mx: 2, mt: 1 }}>{jsonError}</Alert>}
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <Editor
              language="json"
              theme="vs-dark"
              value={jsonValue}
              onChange={(value) => { setJsonValue(value || ''); setJsonError(null); }}
              options={{
                minimap: { enabled: false },
                fontSize: 13,
                lineNumbers: 'on',
                folding: true,
                foldingStrategy: 'indentation',
                showFoldingControls: 'always',
                bracketPairColorization: { enabled: true },
                formatOnPaste: true,
                scrollBeyondLastLine: false,
                wordWrap: 'on',
                tabSize: 2,
                automaticLayout: true,
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5, borderTop: `1px solid ${colors?.border || '#333'}` }}>
          <Button
            onClick={() => setJsonDialogOpen(false)}
            sx={{ color: colors?.textSecondary || '#999', textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleFormatJson}
            sx={{ color: '#f59e0b', textTransform: 'none' }}
          >
            Format
          </Button>
          <Button
            onClick={handleApplyJson}
            variant="contained"
            sx={{ bgcolor: '#22c55e', textTransform: 'none', '&:hover': { bgcolor: '#16a34a' } }}
          >
            Apply to Designer
          </Button>
        </DialogActions>
      </Dialog>
    </DndContext>
  );
}

// ---------------------------------------------------------------------------
// Exported component (wraps with provider)
// ---------------------------------------------------------------------------
export default function VisualWorkspaceDesigner({ workspace, onSave, onClose, colors }) {
  return (
    <DesignerStateProvider>
      <DesignerInner workspace={workspace} onSave={onSave} onClose={onClose} colors={colors} />
    </DesignerStateProvider>
  );
}
